import React from 'react';

/**
 * <BadgeRow>
 *
 * Renders a talent's earned badges (from utils/badges.py via the
 * admin/matching API). Each badge is a small chip with tone-based
 * color and a hover tooltip explaining why it was earned.
 *
 * Props:
 *  - badges: Array<{ slug, label, tier, reason }>
 *  - max?: number — cap visible badges (overflow shown as "+N")
 *  - size?: 'xs' | 'sm' — chip density (default 'xs')
 */
const TONE_MAP = {
  gold:   'bg-amber-100  text-amber-800  border-amber-300',
  blue:   'bg-sky-100    text-sky-800    border-sky-300',
  green:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  purple: 'bg-violet-100 text-violet-800 border-violet-300',
};

const BadgeRow = ({ badges = [], max = 3, size = 'xs' }) => {
  if (!badges || badges.length === 0) return null;
  const visible = badges.slice(0, max);
  const overflow = badges.length - visible.length;
  const sizeClass = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="badge-row">
      {visible.map((b) => (
        <span
          key={b.slug}
          title={b.reason || b.label}
          data-testid={`badge-${b.slug}`}
          className={`inline-flex items-center rounded-full border font-medium uppercase tracking-wide ${sizeClass} ${TONE_MAP[b.tier] || TONE_MAP.blue}`}
        >
          {b.label}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={`inline-flex items-center rounded-full border font-medium ${sizeClass} bg-gray-100 text-gray-600 border-gray-300`}
          title={badges.slice(max).map((b) => b.label).join(', ')}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
};

export default BadgeRow;
